import { FC } from 'react'
import {
  Button, Datagrid, DateField, FunctionField, TextField,
  useGetList, useList, ListContextProvider, Pagination, useTranslate,
  useRefresh, useNotify, useDelete, useRecordContext,
} from 'react-admin'
import { useNavigate } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { IconButton, Tooltip } from '@mui/material'
import { EnabledChip } from './Chips'
import { OrganizationWorkflowLinkOut } from '../types/apiRequests'

interface WorkflowLinksDatagridProps {
  filter: Record<string, unknown>
  createState: Record<string, unknown>
  showOrganization?: boolean
  showWorkflow?: boolean
}

const DeleteRowButton: FC = () => {
  const record = useRecordContext<{ id: string }>()
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()
  const [deleteOne] = useDelete()

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (record?.id && window.confirm(translate('delete.confirmTitle'))) {
      void deleteOne('organization_workflow_link', { id: record.id }, {
        mutationMode: 'pessimistic',
        // eslint-disable-next-line camelcase
        onSuccess: () => { notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: 1 } }); refresh() },
      })
    }
  }

  return (
    <Tooltip title={translate('ra.action.delete')}>
      <IconButton size="small" onClick={handleDelete} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const EditRowButton: FC = () => {
  const record = useRecordContext<{ id: string }>()
  const navigate = useNavigate()
  const translate = useTranslate()

  if (!record?.id) return null

  return (
    <Tooltip title={translate('ra.action.edit')}>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/organization_workflow_link/${String(record.id)}`) }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

export const WorkflowLinksDatagrid: FC<WorkflowLinksDatagridProps> = ({
  filter, createState, showOrganization = false, showWorkflow = false,
}) => {
  const navigate = useNavigate()
  const translate = useTranslate()

  const { data, isLoading } = useGetList<OrganizationWorkflowLinkOut>(
    'organization_workflow_link',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'created', order: 'DESC' }, filter },
    { enabled: Object.values(filter).every(Boolean) }
  )

  const listContext = useList({ data: data ?? [], isLoading, perPage: 25 })

  return (
    <>
      <Button
        label="resources.organization_workflow_link.create"
        onClick={() => navigate('/organization_workflow_link/create', { state: { record: createState } })}
        sx={{ mb: 2 }}
      >
        <AddIcon />
      </Button>
      <ListContextProvider value={listContext}>
        <Datagrid
          bulkActionButtons={false}
          rowClick={(id) => { navigate(`/organization_workflow_link/${String(id)}`); return false }}
        >
          {showOrganization && <TextField source="organization_display_name" label="data.organization" />}
          {showWorkflow && <TextField source="workflow_display_name" label="data.workflow" />}
          <FunctionField label="data.enabled" render={() => <EnabledChip />} />
          <DateField source="created" showTime label="data.createdAt" />
          <EditRowButton />
          <DeleteRowButton />
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </>
  )
}
